import type { ReportingApi as QueueDashboardApi } from '../../../api'
import type {
  CallQueue,
  PaginatedQueuesResponse,
  QueueAgentsResponse,
  QueueCallsResponse,
  QueueStatsResponse,
} from '../types'

// Attach the HTTP status to the thrown error so the shared TanStack retry
// policy (getErrorStatus) can see it. Without it a 429 on these dashboard
// polls reads as statusless, gets retried 3x, and re-amplifies the very
// throttle this targets (support 00389200).
const fetchError = (message: string, status?: number): Error =>
  Object.assign(new Error(message), { status })

/**
 * Transforms paginated /app/v1/extensions/ response to CallQueue[] format
 */
function transformQueuesResponse(data: PaginatedQueuesResponse): CallQueue[] {
  return data.results.map((item) => ({
    id: String(item.id),
    name: item.name,
    extensionNumber: item.number,
  }))
}

export async function fetchQueues(
  api: QueueDashboardApi,
  pbxId: number,
  signal?: AbortSignal,
): Promise<CallQueue[]> {
  if (!pbxId || pbxId <= 0) {
    return []
  }

  const response = await api.getQueueDashboardQueues(pbxId, signal)

  if (!response.ok || !response.data) {
    if (response.status === 404) {
      return []
    }
    throw fetchError(
      `Failed to fetch queues: ${response.status}`,
      response.status,
    )
  }

  return transformQueuesResponse(response.data)
}

export async function fetchQueueCalls(
  api: QueueDashboardApi,
  pbxUuid: string,
  queueNumber: string,
  signal?: AbortSignal,
): Promise<QueueCallsResponse> {
  const response = await api.getQueueDashboardCalls(
    pbxUuid,
    queueNumber,
    signal,
  )

  if (!response.ok || !response.data) {
    throw fetchError(
      `Failed to fetch queue calls: ${response.status}`,
      response.status,
    )
  }

  return response.data
}

export async function fetchQueueAgents(
  api: QueueDashboardApi,
  pbxUuid: string,
  queueNumber: string,
  signal?: AbortSignal,
): Promise<QueueAgentsResponse> {
  const response = await api.getQueueDashboardAgents(
    pbxUuid,
    queueNumber,
    signal,
  )

  if (!response.ok || !response.data) {
    throw fetchError(
      `Failed to fetch queue agents: ${response.status}`,
      response.status,
    )
  }

  return response.data
}

export async function fetchQueueStats(
  api: QueueDashboardApi,
  params: { start: string; end: string; pbxes: string; queue: string },
  signal?: AbortSignal,
): Promise<QueueStatsResponse> {
  const response = await api.getQueueMetrics(params, signal)

  if (!response.ok || !response.data) {
    throw fetchError(
      `Failed to fetch queue metrics: ${response.status}`,
      response.status,
    )
  }

  const { data } = response.data
  const totalCalls = data.total_calls || 0
  const dropRatePercent =
    totalCalls > 0 ? (data.total_canceled / totalCalls) * 100 : 0

  return {
    stats: {
      answeredCount: data.total_answered,
      cancelledCount: data.total_canceled,
      avgWaitSeconds: data.avg_wait_seconds,
      maxWaitSeconds: data.max_wait_seconds,
      avgCancelledSeconds: data.avg_cancel_seconds,
      maxCancelledSeconds: data.max_cancel_seconds,
      dropRatePercent,
    },
  }
}
